import React, { useState, useRef, useMemo } from 'react';
import { UserProfile, UserRole, BrandAsset, AssetType, GridSize } from '../types';
import { Button, Card, Input, Badge } from '../components/UI';
import { ColorPalette } from './ColorPalette';
import { generateBrandBackground } from '../services/geminiService';
import { cleanSVG, colorizeSVG, convertSvgToBase64 } from '../utils/svgHelpers';
import { GRID_MAPPING } from '../constants';
import { 
  Plus, Image as ImageIcon, Type, Wand2, Trash2, 
  ChevronUp, ChevronRight, ChevronDown, ChevronLeft, 
  Save, Undo2, Loader2, Lock, Unlock, Upload, LayoutGrid,
  Palette, X, Check, Maximize2, Minimize2, CheckCircle2, Circle
} from 'lucide-react';

interface DashboardProps {
  user: UserProfile;
  role: UserRole;
  onUpdateUser: (user: UserProfile) => void;
  onOpenMedia: () => void;
  onUndo?: () => void;
  canUndo?: boolean;
  saveStatus?: 'saved' | 'saving' | 'error';
  isPublicView?: boolean;
}

// --- SUBCOMPONENTS ---

const SetupGuide: React.FC<{ user: UserProfile, onClose: () => void }> = ({ user, onClose }) => {
  const steps = [
    { id: 'colors', label: 'Define Brand Palette', completed: user.colors.length >= 2 },
    { id: 'logo', label: 'Upload Vector Logo', completed: user.assets.some(a => a.type === AssetType.LOGO_LIGHT || a.type === AssetType.LOGO_DARK) },
    { id: 'text', label: 'Add Typography Sample', completed: user.assets.some(a => a.type === AssetType.TEXT) },
    { id: 'cover', label: 'Generate AI Background', completed: user.assets.some(a => a.type === AssetType.GENERATED_MOCKUP) },
  ];

  const progress = Math.round((steps.filter(s => s.completed).length / steps.length) * 100);
  if (progress === 100) return null;

  return (
    <div className="bg-white rounded-2xl p-6 border border-brand-text/5 shadow-sm mb-8 relative overflow-hidden animate-fade-in-up">
      <div className="absolute top-0 left-0 w-1 h-full bg-brand-primary"></div>
      <button onClick={onClose} className="absolute top-4 right-4 text-gray-300 hover:text-gray-500"><X size={16}/></button>
      
      <div className="flex flex-col md:flex-row gap-8 items-center">
        <div className="flex-1">
          <h3 className="text-xl font-bold mb-2">Getting Started</h3>
          <p className="text-gray-500 mb-4">Complete your brand grid setup to enable one-click export.</p>
          <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
            <div className="bg-brand-primary h-full transition-all duration-1000" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 justify-center md:justify-end">
          {steps.map(step => (
            <div key={step.id} className={`flex items-center gap-2 px-4 py-2 rounded-full border ${step.completed ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 text-gray-400'}`}>
              {step.completed ? <CheckCircle2 size={16}/> : <Circle size={16}/>}
              <span className="text-sm font-medium">{step.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EditorToolbar: React.FC<{ 
  asset: BrandAsset, 
  colors: any[], 
  onUpdate: (id: string, updates: Partial<BrandAsset>) => void,
  onClose: () => void,
  onDelete: () => void
}> = ({ asset, colors, onUpdate, onClose, onDelete }) => {
  
  const isSvg = asset.url.includes('image/svg+xml');
  const canColorize = isSvg && (asset.type.includes('ICON') || asset.type.includes('LOGO'));

  const handleRecolor = (hex: string) => {
     try {
       const base64Data = asset.url.split(',')[1];
       const rawSvg = atob(base64Data);
       const newSvg = colorizeSVG(rawSvg, hex);
       onUpdate(asset.id, { url: convertSvgToBase64(newSvg) });
     } catch (e) {
       console.error("Failed to recolor", e);
     }
  };

  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
      <div className="bg-brand-text/95 backdrop-blur-md text-white p-2 rounded-3xl shadow-2xl flex items-center gap-4 pr-6 pl-6 py-3 border border-white/10">
        
        {/* Close */}
        <button onClick={onClose} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
          <Check size={16} />
        </button>
        
        <div className="h-8 w-px bg-white/10"></div>

        {/* Padding Control */}
        <div className="flex flex-col gap-1 w-32">
          <div className="flex justify-between text-[10px] font-bold text-white/50 uppercase">
            <span>Padding</span>
            <span>{asset.padding ?? 0}px</span>
          </div>
          <input 
            type="range" 
            min="0" 
            max="128" 
            step="4"
            value={asset.padding ?? 0}
            onChange={(e) => onUpdate(asset.id, { padding: parseInt(e.target.value) })}
            className="accent-brand-primary h-1 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div className="h-8 w-px bg-white/10"></div>

        {/* Background Color */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-white/50 uppercase">Background</span>
          <div className="flex gap-1">
            <button 
                onClick={() => onUpdate(asset.id, { backgroundColor: 'transparent' })}
                className="w-5 h-5 rounded-full border border-white/20 bg-transparent relative overflow-hidden hover:scale-110 transition-transform"
                title="Transparent"
            >
                <div className="absolute inset-0 border-r border-red-500 rotate-45 transform origin-center opacity-50"></div>
            </button>
            {colors.map(c => (
              <button
                  key={c.id}
                  onClick={() => onUpdate(asset.id, { backgroundColor: c.hex })}
                  className="w-5 h-5 rounded-full border border-white/10 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        </div>

        {/* Foreground Color (Only for SVGs) */}
        {canColorize && (
          <>
            <div className="h-8 w-px bg-white/10"></div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold text-white/50 uppercase">Icon Color</span>
              <div className="flex gap-1">
                {colors.map(c => (
                  <button
                      key={c.id}
                      onClick={() => handleRecolor(c.hex)}
                      className="w-5 h-5 rounded-full border border-white/10 hover:scale-110 transition-transform"
                      style={{ backgroundColor: c.hex }}
                  />
                ))}
                <button
                    onClick={() => handleRecolor('#ffffff')}
                    className="w-5 h-5 rounded-full border border-white/10 bg-white hover:scale-110 transition-transform"
                />
                <button
                    onClick={() => handleRecolor('#000000')}
                    className="w-5 h-5 rounded-full border border-white/10 bg-black hover:scale-110 transition-transform"
                />
              </div>
            </div>
          </>
        )}

        <div className="h-8 w-px bg-white/10"></div>

        {/* Grid Size Actions */}
        <div className="flex gap-1">
           <button 
             onClick={() => onUpdate(asset.id, { isPasswordProtected: !asset.isPasswordProtected })}
             className={`p-2 rounded-xl transition-colors ${asset.isPasswordProtected ? 'bg-brand-primary text-white' : 'hover:bg-white/10 text-white/50'}`}
             title="Password Protect"
           >
             {asset.isPasswordProtected ? <Lock size={16}/> : <Unlock size={16}/>}
           </button>
           <button 
             onClick={onDelete}
             className="p-2 hover:bg-red-500/20 text-red-400 hover:text-red-400 rounded-xl transition-colors"
             title="Delete Asset"
           >
             <Trash2 size={16}/>
           </button>
        </div>

      </div>
    </div>
  );
};


// --- MAIN DASHBOARD ---

export const Dashboard: React.FC<DashboardProps> = ({ 
  user, 
  role, 
  onUpdateUser, 
  onOpenMedia,
  onUndo,
  canUndo,
  saveStatus,
  isPublicView = false
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [showSetupGuide, setShowSetupGuide] = useState(true);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const canEdit = !isPublicView && (role === UserRole.ADMIN || role === UserRole.AGENCY || role === UserRole.USER);
  const effectivePreviewMode = isPublicView;

  // --- HELPERS ---

  const updateAssets = (newAssets: BrandAsset[]) => {
    onUpdateUser({ ...user, assets: newAssets });
  };

  const updateAsset = (id: string, updates: Partial<BrandAsset>) => {
    const newAssets = user.assets.map(a => a.id === id ? { ...a, ...updates } : a);
    updateAssets(newAssets);
  };

  const updateTextAsset = (id: string, textUpdates: Partial<any>) => {
     const asset = user.assets.find(a => a.id === id);
     if (!asset || !asset.textData) return;
     updateAsset(id, { textData: { ...asset.textData, ...textUpdates } });
  };

  const toggleChevron = (id: string, dir: 'top' | 'right' | 'bottom' | 'left') => {
    const asset = user.assets.find(a => a.id === id);
    if (!asset || !asset.textData) return;
    updateTextAsset(id, { chevrons: { ...asset.textData.chevrons, [dir]: !asset.textData.chevrons[dir] } });
  };

  const deleteAsset = (id: string) => {
    const newAssets = user.assets.map(a => a.id === id ? { ...a, deletedAt: new Date().toISOString() } : a);
    updateAssets(newAssets);
    if (editingAssetId === id) setEditingAssetId(null);
  };

  // --- UPLOAD HANDLERS ---

  const handleAppIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'image/svg+xml') {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const rawSvg = atob(result.split(',')[1]);
        const cleanRaw = cleanSVG(rawSvg);
        
        const primary = user.colors[0]?.hex || '#000000';
        const white = '#ffffff';

        const newAssets: BrandAsset[] = [];
        
        // 1. Full Color Original
        newAssets.push({
           id: `icon-${Date.now()}-orig`,
           type: AssetType.ICON_LIGHT,
           url: convertSvgToBase64(cleanRaw), 
           gridSize: GridSize.SQUARE_MD,
           isPasswordProtected: false,
           order: user.assets.length,
           backgroundColor: 'transparent',
           padding: 16
        });

        // 2. White on Primary
        newAssets.push({
           id: `icon-${Date.now()}-var1`,
           type: AssetType.ICON_DARK,
           url: convertSvgToBase64(colorizeSVG(cleanRaw, white)),
           gridSize: GridSize.SQUARE_MD,
           isPasswordProtected: false,
           order: user.assets.length + 1,
           backgroundColor: primary,
           padding: 16
        });

        // 3. Primary on White
        newAssets.push({
           id: `icon-${Date.now()}-var2`,
           type: AssetType.ICON_LIGHT,
           url: convertSvgToBase64(colorizeSVG(cleanRaw, primary)),
           gridSize: GridSize.SQUARE_MD,
           isPasswordProtected: false,
           order: user.assets.length + 2,
           backgroundColor: white,
           padding: 16
        });

        updateAssets([...user.assets, ...newAssets]);
      };
      reader.readAsDataURL(file);
    }
    if(iconInputRef.current) iconInputRef.current.value = '';
  };

  const handleBrandLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'image/svg+xml') {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const rawSvg = atob(result.split(',')[1]);
        const cleanRaw = cleanSVG(rawSvg);

        const primary = user.colors[0]?.hex || '#000000';
        const white = '#ffffff';

        const newAssets: BrandAsset[] = [];

        // 1. Full Color
        newAssets.push({
           id: `logo-${Date.now()}-orig`,
           type: AssetType.LOGO_LIGHT,
           url: convertSvgToBase64(cleanRaw),
           gridSize: GridSize.LANDSCAPE_MD,
           isPasswordProtected: false,
           order: user.assets.length,
           padding: 32,
           backgroundColor: 'transparent'
        });

        // 2. White on Primary
        newAssets.push({
           id: `logo-${Date.now()}-var1`,
           type: AssetType.LOGO_DARK,
           url: convertSvgToBase64(colorizeSVG(cleanRaw, white)),
           gridSize: GridSize.LANDSCAPE_MD,
           isPasswordProtected: false,
           order: user.assets.length + 1,
           backgroundColor: primary,
           padding: 32
        });

        updateAssets([...user.assets, ...newAssets]);
      };
      reader.readAsDataURL(file);
    }
    if(logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleGenericUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
           updateAssets([...user.assets, {
              id: `upload-${Date.now()}`,
              type: AssetType.ICON_LIGHT,
              url: event.target.result as string,
              gridSize: GridSize.SQUARE_MD,
              isPasswordProtected: false,
              order: user.assets.length,
              padding: 0
           }]);
        }
      };
      reader.readAsDataURL(file);
    }
    if(fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (!genPrompt.trim()) return;
    setIsGenerating(true);
    try {
      const brandColors = user.colors.filter(c => c.visible).map(c => c.hex);
      const generatedImage = await generateBrandBackground(genPrompt, brandColors);
      if (generatedImage) {
        updateAssets([...user.assets, {
            id: `gen-${Date.now()}`,
            type: AssetType.GENERATED_MOCKUP,
            url: generatedImage,
            gridSize: GridSize.LANDSCAPE_SM,
            isPasswordProtected: false,
            order: user.assets.length,
            prompt: genPrompt,
            padding: 0
        }]);
        setGenPrompt('');
      }
    } catch (e) {
      alert("Generation failed.");
    } finally {
      setIsGenerating(false);
    }
  };

  const addTextAsset = () => {
    updateAssets([...user.assets, {
      id: `text-${Date.now()}`,
      type: AssetType.TEXT,
      url: '',
      gridSize: GridSize.SQUARE_MD,
      isPasswordProtected: false,
      order: user.assets.length,
      textData: {
        title: 'HEADING',
        body: 'Click to edit',
        chevrons: { top: false, right: false, bottom: false, left: false },
        alignment: 'center'
      }
    }]);
  };

  const visibleAssets = user.assets.filter(a => !a.deletedAt);
  const editingAsset = visibleAssets.find(a => a.id === editingAssetId);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-8 pb-40">
      
      {/* FLOATING EDITOR TOOLBAR */}
      {canEdit && editingAsset && (
        <EditorToolbar 
          asset={editingAsset}
          colors={user.colors}
          onUpdate={updateAsset}
          onClose={() => setEditingAssetId(null)}
          onDelete={() => deleteAsset(editingAsset.id)}
        />
      )}

      {/* DASHBOARD HEADER */}
      {!isPublicView && (
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 bg-white p-6 rounded-3xl shadow-sm border border-brand-text/5">
           
           <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto items-center">
              <div className="flex gap-2 mr-4">
                 <Button variant="ghost" disabled={!canUndo} onClick={onUndo} title="Undo" className="bg-gray-50">
                    <Undo2 size={20} />
                 </Button>
                 <div className="px-4 py-2 text-xs font-bold uppercase text-brand-text/50 flex items-center gap-2 bg-gray-50 rounded-xl">
                    {saveStatus === 'saving' && <><Loader2 className="animate-spin" size={12}/> Saving...</>}
                    {saveStatus === 'saved' && <><Save size={12}/> Saved</>}
                    {saveStatus === 'error' && <span className="text-red-500">Error</span>}
                 </div>
              </div>

              <div className="relative flex-1 min-w-[300px]">
                <Input 
                  placeholder="Describe a scene to generate..." 
                  value={genPrompt}
                  onChange={(e) => setGenPrompt(e.target.value)}
                  disabled={isGenerating}
                  className="pr-12"
                />
                <button 
                  onClick={handleGenerate}
                  disabled={isGenerating || !genPrompt}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-primary disabled:opacity-30 hover:scale-110 transition-transform p-1"
                >
                  {isGenerating ? <Loader2 className="animate-spin" size={20}/> : <Wand2 size={20}/>}
                </button>
              </div>
           </div>

           <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto justify-center xl:justify-end">
              <div className="h-8 w-px bg-gray-200 mx-2 hidden xl:block"></div>

              {/* App Icon Upload */}
              <input type="file" ref={iconInputRef} className="hidden" accept=".svg" onChange={handleAppIconUpload} />
              <Button variant="outline" size="sm" onClick={() => iconInputRef.current?.click()}>
                 <LayoutGrid size={16}/> App Icon <span className="text-[10px] opacity-50 ml-1">(SVG)</span>
              </Button>

              {/* Logo Upload */}
              <input type="file" ref={logoInputRef} className="hidden" accept=".svg" onChange={handleBrandLogoUpload} />
              <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                 <ImageIcon size={16}/> Logo <span className="text-[10px] opacity-50 ml-1">(SVG)</span>
              </Button>

              {/* Generic Upload */}
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleGenericUpload} />
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                 <Upload size={16}/> Photo
              </Button>
              
              <Button variant="secondary" size="sm" onClick={addTextAsset}>
                 <Type size={16}/> Text
              </Button>
           </div>
        </div>
      )}
      
      {/* SETUP GUIDE (If applicable) */}
      {!isPublicView && showSetupGuide && (
         <SetupGuide user={user} onClose={() => setShowSetupGuide(false)} />
      )}

      <ColorPalette 
         colors={user.colors} 
         onChange={(newColors) => onUpdateUser({ ...user, colors: newColors })} 
         readOnly={!canEdit}
      />

      {/* GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 auto-rows-[minmax(100px,auto)]">
        {visibleAssets.map(asset => {
           // Helper to determine if we can edit colors for this asset
           const isSvg = asset.url.includes('image/svg+xml');
           const isEditing = editingAssetId === asset.id;

           return (
             <Card 
               key={asset.id} 
               className={`
                 ${GRID_MAPPING[asset.gridSize]} 
                 relative !p-0 overflow-hidden group transition-all duration-300 transform-gpu
                 ${asset.isPasswordProtected && effectivePreviewMode ? 'blur-md pointer-events-none select-none' : ''}
                 ${isEditing ? 'ring-4 ring-brand-primary ring-offset-4 z-30 scale-[1.02] shadow-2xl' : ''}
                 ${!isEditing && canEdit ? 'hover:scale-[1.01] hover:shadow-lg cursor-pointer' : ''}
               `}
               onClick={() => {
                  if (canEdit && !effectivePreviewMode) setEditingAssetId(asset.id);
               }}
             >
                {/* Indicator that asset is protected */}
                {asset.isPasswordProtected && (
                  <div className="absolute top-2 right-2 z-10 bg-brand-text/80 text-white p-1 rounded">
                     <Lock size={12}/>
                  </div>
                )}

                {/* TEXT ASSET RENDERER */}
                {asset.type === AssetType.TEXT && asset.textData ? (
                   <div className="w-full h-full relative group/text flex flex-col items-center justify-center p-4">
                     {(['top', 'right', 'bottom', 'left'] as const).map(dir => {
                        const isActive = asset.textData!.chevrons[dir];
                        const Icon = dir === 'top' ? ChevronUp : dir === 'right' ? ChevronRight : dir === 'bottom' ? ChevronDown : ChevronLeft;
                        const posClass = 
                          dir === 'top' ? 'top-0 left-1/2 -translate-x-1/2' :
                          dir === 'right' ? 'right-0 top-1/2 -translate-y-1/2' :
                          dir === 'bottom' ? 'bottom-0 left-1/2 -translate-x-1/2' :
                          'left-0 top-1/2 -translate-y-1/2';

                        if (!isActive && effectivePreviewMode) return null;

                        return (
                           <div 
                             key={dir}
                             onClick={(e) => {
                                if (canEdit && !effectivePreviewMode) {
                                  e.stopPropagation();
                                  toggleChevron(asset.id, dir);
                                }
                             }}
                             className={`
                                absolute ${posClass} transition-all duration-300 z-0 flex items-center justify-center
                                ${isActive 
                                  ? 'text-brand-primary opacity-100 scale-100' 
                                  : 'text-gray-200 opacity-0 group-hover/text:opacity-100 cursor-pointer scale-90'
                                }
                                ${effectivePreviewMode ? 'pointer-events-none' : ''}
                             `}
                           >
                              <Icon size={asset.gridSize.includes('SQUARE_SM') ? 32 : 64} strokeWidth={4} />
                           </div>
                        );
                     })}

                     <div className="z-10 relative w-full h-full flex flex-col items-center justify-center px-4">
                       <input 
                         value={asset.textData.title}
                         onChange={(e) => updateTextAsset(asset.id, { title: e.target.value })}
                         className="w-full bg-transparent text-xl md:text-3xl font-black mb-2 outline-none placeholder:text-gray-200 text-center uppercase tracking-tight text-brand-text"
                         placeholder="HEADING"
                         disabled={!canEdit || effectivePreviewMode}
                       />
                       <textarea
                         value={asset.textData.body}
                         onChange={(e) => updateTextAsset(asset.id, { body: e.target.value })}
                         className="w-full bg-transparent resize-none outline-none text-sm md:text-lg font-medium text-brand-text/70 placeholder:text-gray-200 text-center overflow-hidden"
                         placeholder="Body text..."
                         disabled={!canEdit || effectivePreviewMode}
                         rows={2}
                       />
                     </div>
                   </div>
                 ) : (
                   /* IMAGE/SVG ASSET RENDERER */
                   <div 
                      className="w-full h-full relative flex items-center justify-center transition-all duration-300" 
                      style={{ backgroundColor: asset.backgroundColor || 'transparent' }}
                   >
                     <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ padding: `${asset.padding ?? 0}px` }}
                     >
                       <img 
                         src={asset.url} 
                         alt="Asset" 
                         className={`w-full h-full ${asset.url.includes('svg') ? 'object-contain' : 'object-cover'} pointer-events-none select-none`}
                       />
                     </div>
                     
                     {/* Edit Hint Overlay */}
                     {canEdit && !isEditing && !effectivePreviewMode && (
                        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                           <div className="bg-white/90 text-brand-text px-3 py-1 rounded-full text-xs font-bold shadow-sm flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                             <Palette size={12}/> Edit Style
                           </div>
                        </div>
                     )}
                   </div>
                 )}
             </Card>
           );
        })}
        {visibleAssets.length === 0 && (
          <div className="col-span-full py-20 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-3xl">
            <p className="mb-4">No assets yet.</p>
            {canEdit && <Button onClick={addTextAsset}>Add your first asset</Button>}
          </div>
        )}
      </div>
    </div>
  );
};